import { useCallback } from "react";

import {
  format,
  formatDistanceStrict,
  FormatDistanceStrictOptions,
} from "date-fns";
import { useTranslation } from "react-i18next";

import useFnsLocale from "./useFnsLocale";

type DateIsh = Date | number | string;

export type FormatterType = (date: DateIsh, formatStr: string) => string;

export function useFormat() {
  const { fnsLocale } = useFnsLocale();
  const dateTimeFormat: FormatterType = useCallback(
    (date, formatStr): string =>
      format(date, formatStr, {
        locale: fnsLocale,
      }),
    [fnsLocale],
  );

  return { format: dateTimeFormat };
}

export function useFormatDistance() {
  const { fnsLocale } = useFnsLocale();

  return useCallback(
    (
      date: DateIsh,
      earlierDate: DateIsh,
      options?: FormatDistanceStrictOptions,
    ): string =>
      formatDistanceStrict(date, earlierDate, {
        locale: fnsLocale,
        ...options,
      }),
    [fnsLocale],
  );
}

export function useFormatDurationShort() {
  const { t } = useTranslation();

  return useCallback(
    (durationSeconds: number): string => {
      const hours = Math.floor(durationSeconds / 3_600);
      const remainingSeconds = durationSeconds % 3_600;
      const minutes = Math.floor(remainingSeconds / 60);

      if (hours) {
        return `${hours}${t("h")} ${minutes}${t("m")}`;
      }

      return `${minutes}${t("m")}`;
    },
    [t],
  );
}
